import React, { useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { GraphVisualization } from '../components/GraphVisualization';
import { LoadingSpinner } from '../components/LoadingSpinner';

const GET_FULL_GRAPH = gql`
  query GetFullGraph {
    fullGraph {
      nodes {
        id
        name
        version
        type
        tags
      }
      links {
        source
        target
        type
        label
      }
    }
  }
`;

export function GraphPage() {
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useQuery(GET_FULL_GRAPH);
  const [selectedNode, setSelectedNode] = useState<any>(null);

  const handleNodeClick = (node: any) => {
    setSelectedNode(node);
  };

  const handleViewDetails = () => {
    if (selectedNode) {
      navigate(`/context/${selectedNode.id}`);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold mb-4">Context Graph Visualization</h1>
        <LoadingSpinner message="Loading graph..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold mb-4">Context Graph Visualization</h1>
        <div className="border border-red-200 rounded-lg p-6 bg-red-50">
          <div className="text-4xl mb-2">⚠️</div>
          <h3 className="text-lg font-semibold mb-2">Error Loading Graph</h3>
          <p className="text-red-600 mb-4">{error.message}</p>
          <button
            onClick={() => refetch()}
            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const graphData = data?.fullGraph || { nodes: [], links: [] };

  if (graphData.nodes.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold mb-4">Context Graph Visualization</h1>
        <div className="border rounded-lg p-12 text-center bg-gray-50">
          <div className="text-4xl mb-4">📊</div>
          <h3 className="text-lg font-semibold mb-2">No Contexts Yet</h3>
          <p className="text-gray-600">
            Create some contexts and add dependencies to see the graph visualization
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 h-screen flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">
          Context Graph Visualization
          <span className="ml-2 text-sm font-normal text-gray-500">
            ({graphData.nodes.length} contexts, {graphData.links.length} relationships)
          </span>
        </h1>
        <button
          onClick={() => refetch()}
          className="bg-primary text-white px-4 py-2 rounded hover:bg-primary/90 text-sm"
        >
          Refresh
        </button>
      </div>

      <div className="flex-1 min-h-0 relative">
        <GraphVisualization data={graphData} onNodeClick={handleNodeClick} />
      </div>

      {selectedNode && (
        <div className="fixed bottom-4 right-4 bg-white border-2 border-primary rounded-lg p-4 shadow-xl max-w-sm">
          <div className="flex justify-between items-start mb-2">
            <h3 className="text-lg font-bold">{selectedNode.name}</h3>
            <button
              onClick={() => setSelectedNode(null)}
              className="text-gray-400 hover:text-gray-600 text-xl leading-none"
            >
              ✕
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-2 capitalize">{selectedNode.type}</p>
          <p className="text-sm text-gray-500 mb-3">Version: {selectedNode.version}</p>
          {selectedNode.tags.length > 0 && (
            <div className="flex gap-2 flex-wrap mb-3">
              {selectedNode.tags.map((tag: string) => (
                <span key={tag} className="text-xs bg-gray-100 px-2 py-1 rounded">
                  {tag}
                </span>
              ))}
            </div>
          )}
          <button
            onClick={handleViewDetails}
            className="w-full bg-primary text-white px-4 py-2 rounded hover:bg-primary/90 text-sm"
          >
            View Details
          </button>
        </div>
      )}
    </div>
  );
}
